// keyrd_console/src/components/PilotReviewActionPanel.js
import React, { useMemo, useState } from "react";
import { submitPilotPreflightReview } from "../api/pilotAdmin";
import PilotStatusPill from "./PilotStatusPill";

const panelStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 14,
};

const titleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  color: "#0f172a",
};

const helpStyle = {
  fontSize: 13,
  color: "#475569",
  lineHeight: 1.5,
};

const warningStyle = {
  padding: 12,
  borderRadius: 10,
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fdba74",
  fontSize: 13,
  fontWeight: 600,
};

const rowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  alignItems: "center",
};

const labelStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: "#334155",
};

const selectStyle = {
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  minWidth: 240,
};

const textareaStyle = {
  width: "100%",
  minHeight: 110,
  padding: 12,
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  resize: "vertical",
  fontFamily: "inherit",
  fontSize: 14,
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  fontSize: 14,
};

const buttonRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
};

const buttonStyle = (tone = "default", disabled = false) => {
  const palette =
    tone === "approve"
      ? {
          background: "#dcfce7",
          color: "#166534",
          border: "#86efac",
        }
      : tone === "hold"
      ? {
          background: "#fef3c7",
          color: "#92400e",
          border: "#fcd34d",
        }
      : tone === "reject"
      ? {
          background: "#fee2e2",
          color: "#b91c1c",
          border: "#fca5a5",
        }
      : {
          background: "#f8fafc",
          color: "#0f172a",
          border: "#cbd5e1",
        };

  return {
    padding: "10px 14px",
    borderRadius: 10,
    border: `1px solid ${palette.border}`,
    background: disabled ? "#f8fafc" : palette.background,
    color: disabled ? "#94a3b8" : palette.color,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
  };
};

const successStyle = {
  padding: 12,
  borderRadius: 10,
  background: "#f0fdf4",
  color: "#166534",
  border: "1px solid #86efac",
  fontSize: 13,
  fontWeight: 600,
};

const errorStyle = {
  padding: 12,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
  fontSize: 13,
  fontWeight: 600,
};

const REVIEW_OPTIONS = [
  {
    value: "approved_for_next_stage_review",
    label: "Approve for Next Stage Review",
    severity: "info",
    defaultReason: "readiness_gates_passed",
    tone: "approve",
  },
  {
    value: "hold",
    label: "Hold",
    severity: "warning",
    defaultReason: "requires_operator_follow_up",
    tone: "hold",
  },
  {
    value: "rejected_needs_correction",
    label: "Reject / Needs Correction",
    severity: "high",
    defaultReason: "readiness_incomplete_or_incorrect",
    tone: "reject",
  },
];

function outcomeMeta(outcome) {
  return (
    REVIEW_OPTIONS.find((item) => item.value === outcome) || REVIEW_OPTIONS[0]
  );
}

function needsReason(outcome) {
  return outcome === "hold" || outcome === "rejected_needs_correction";
}

function PilotReviewActionPanel({
  auth,
  participantId,
  initialEvidenceState = "",
  initialEvidenceConfidence = "",
  onReviewSubmitted,
}) {
  const [reviewOutcome, setReviewOutcome] = useState(
    "approved_for_next_stage_review"
  );
  const [reasonCode, setReasonCode] = useState("readiness_gates_passed");
  const [comment, setComment] = useState("");
  const [evidenceState, setEvidenceState] = useState(initialEvidenceState || "");
  const [evidenceConfidence, setEvidenceConfidence] = useState(
    initialEvidenceConfidence || ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const meta = useMemo(() => outcomeMeta(reviewOutcome), [reviewOutcome]);

  const validationError = useMemo(() => {
    if (!participantId) return "No participant selected.";
    if (needsReason(reviewOutcome) && !String(reasonCode || "").trim()) {
      return "A reason code is required for hold and reject decisions.";
    }
    return "";
  }, [participantId, reviewOutcome, reasonCode]);

  const handleOutcomeChange = (nextOutcome) => {
    const nextMeta = outcomeMeta(nextOutcome);
    setReviewOutcome(nextOutcome);

    const currentTrimmed = String(reasonCode || "").trim();
    const currentWasDefault = REVIEW_OPTIONS.some(
      (item) => item.defaultReason === currentTrimmed
    );

    if (!currentTrimmed || currentWasDefault) {
      setReasonCode(nextMeta.defaultReason);
    }

    setSuccess("");
    setError("");
  };

  const handleSubmit = async () => {
    setSuccess("");
    setError("");

    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        outcome: reviewOutcome,
        event_type: "pilot_preflight_review",
        reason_code: String(reasonCode || "").trim(),
        severity: meta.severity,
        review_comment: String(comment || "").trim(),
        evidence_state: String(evidenceState || "").trim(),
        evidence_confidence: String(evidenceConfidence || "").trim(),
      };

      const res = await submitPilotPreflightReview(participantId, payload, auth);
      const data = res?.data || res || {};

      setSuccess(
        data?.message ||
          "Review recorded. No activation occurred; review-only state preserved."
      );

      if (typeof onReviewSubmitted === "function") {
        onReviewSubmitted(data);
      }
    } catch (err) {
      setError(err?.message || "Failed to submit pilot preflight review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={panelStyle}>
      <h2 style={titleStyle}>Review Actions</h2>

      <div style={helpStyle}>
        This panel records explicit operator review decisions only. It must not
        activate participants, trigger nudges, or mutate RL state.
      </div>

      <div style={warningStyle}>
        Review-only control surface. Approval here does not activate live
        intervention.
      </div>

      <div style={rowStyle}>
        <div style={labelStyle}>Selected Outcome</div>
        <PilotStatusPill status={reviewOutcome} />
      </div>

      <div>
        <div style={labelStyle}>Review Outcome</div>
        <div style={{ height: 6 }} />
        <select
          style={selectStyle}
          value={reviewOutcome}
          onChange={(e) => handleOutcomeChange(e.target.value)}
          disabled={submitting}
        >
          {REVIEW_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div style={labelStyle}>Reason Code</div>
        <div style={{ height: 6 }} />
        <input
          style={inputStyle}
          type="text"
          value={reasonCode}
          onChange={(e) => setReasonCode(e.target.value)}
          placeholder="e.g. readiness_gates_passed"
          disabled={submitting}
        />
      </div>

      <div>
        <div style={labelStyle}>Evidence State</div>
        <div style={{ height: 6 }} />
        <input
          style={inputStyle}
          type="text"
          value={evidenceState}
          onChange={(e) => setEvidenceState(e.target.value)}
          placeholder="e.g. sufficient_shadow_signal"
          disabled={submitting}
        />
      </div>

      <div>
        <div style={labelStyle}>Evidence Confidence</div>
        <div style={{ height: 6 }} />
        <input
          style={inputStyle}
          type="text"
          value={evidenceConfidence}
          onChange={(e) => setEvidenceConfidence(e.target.value)}
          placeholder="e.g. high"
          disabled={submitting}
        />
      </div>

      <div>
        <div style={labelStyle}>Review Comment</div>
        <div style={{ height: 6 }} />
        <textarea
          style={textareaStyle}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Optional operator note. This should explain the decision, not imply activation."
          disabled={submitting}
        />
      </div>

      {validationError ? <div style={errorStyle}>{validationError}</div> : null}
      {error ? <div style={errorStyle}>{error}</div> : null}
      {success ? <div style={successStyle}>{success}</div> : null}

      <div style={buttonRowStyle}>
        <button
          style={buttonStyle("approve", submitting)}
          onClick={() => {
            handleOutcomeChange("approved_for_next_stage_review");
            setTimeout(handleSubmit, 0);
          }}
          disabled={submitting}
        >
          Approve for Next Stage Review
        </button>

        <button
          style={buttonStyle("hold", submitting)}
          onClick={() => {
            handleOutcomeChange("hold");
            setTimeout(handleSubmit, 0);
          }}
          disabled={submitting}
        >
          Hold
        </button>

        <button
          style={buttonStyle("reject", submitting)}
          onClick={() => {
            handleOutcomeChange("rejected_needs_correction");
            setTimeout(handleSubmit, 0);
          }}
          disabled={submitting}
        >
          Reject / Needs Correction
        </button>
      </div>
    </div>
  );
}

export default PilotReviewActionPanel;